import type { ButtonHTMLAttributes, ReactNode } from 'react';

import Spinner from '@/components/Spinner/Spinner';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-(--accent-primary-strong) text-(--text-on-accent) hover:bg-(--accent-primary) active:translate-y-px',
  secondary: 'bg-(--bg-surface-strong) text-(--text-primary) border border-(--border-default) hover:bg-(--bg-surface-hover)',
  ghost: 'bg-transparent text-(--text-secondary) hover:bg-(--bg-surface-hover) hover:text-(--text-primary)',
  destructive: 'bg-(--accent-danger) text-(--text-on-accent) hover:opacity-90',
  link: 'bg-transparent text-(--accent-primary) hover:underline px-0 py-0',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-6 text-base gap-2 rounded-xl',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leadingIcon,
  trailingIcon,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variant !== 'link' && sizeStyles[size],
        variantStyles[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
}
